<script lang="ts">
	import { stopPropagation } from 'svelte/legacy'

	import Hamburger from '$lib/icons/Hamburger.svelte'
	import MobileMenuButton from '$lib/app/header/MobileMenuButton.svelte'
	import { CORE_MENU_ITEMS, MOBILE_MENU_ITEMS } from '$lib/app/header/constants/menu-items.constant'
	import { fade } from 'svelte/transition'
	import Footer from '$lib/components/elements/Footer.svelte'
	import Logo from '$lib/icons/Logo.svelte'
	import { CircleUserRound } from 'lucide-svelte'
	import type { LayoutData } from './$types'
	import { page } from '$app/state'
	interface Props {
		data: LayoutData
		children?: import('svelte').Snippet
	}

	let { data, children }: Props = $props()

	let { session, user } = $derived(data)

	let mobileMenuOpen = $state(false)

	const handleMenuOpen = () => {
		if (mobileMenuOpen) {
			handleMenuClose()
			return
		}
		mobileMenuOpen = true
		document.body.addEventListener('click', handleMenuClose)
	}
	const handleMenuClose = () => {
		mobileMenuOpen = false
		document.body.removeEventListener('click', handleMenuClose)
	}

	const buildUrl = (urlPath: string) => {
		const queryParams = page.url.searchParams.toString()
			? `?${page.url.searchParams.toString()}`
			: ''

		if (urlPath.includes('#')) {
			return `/${queryParams}` + urlPath.split('/')[1]
		}

		return urlPath + queryParams
	}
</script>

<nav class="bg-white sticky top-0 border-slate-900/10 border-b z-20">
	<div>
		<div
			class="max-w-screen-xl flex flex-wrap md:flex-col lg:flex-row md:gap-6 lg:gap-0 items-center justify-between mx-auto md:pt-10 lg:pt-4 pt-4 pb-4 px-6 lg:px-8 relative"
		>
			<a href="/" class="flex items-center">
				<div class="w-40 lg:w-52">
					<Logo />
				</div>
			</a>
			<button
				type="button"
				class="inline-flex items-center p-2 w-10 h-10 justify-center text-sm text-gray-500 rounded-lg md:hidden hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:text-gray-400"
				aria-controls="navbar-default"
				aria-expanded={mobileMenuOpen}
				onclick={stopPropagation(() => {
					handleMenuOpen()
				})}
			>
				<Hamburger />
			</button>
			<div class="w-full md:block md:w-auto hidden">
				<ul
					class="font-medium flex flex-row p-0 border-gray-100 rounded-lg md:space-x-4 lg:space-x-6 mt-0 border-0 bg-white items-center"
				>
					{#each CORE_MENU_ITEMS as menuItem}
						<li class="relative">
							<MobileMenuButton href={buildUrl(menuItem.href)}>
								{menuItem.title}
							</MobileMenuButton>
							{#if menuItem.href === '/premium'}
								<span class="absolute flex h-2 w-2 -top-1 right-0 -mr-3">
									<span
										class="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"
									></span>
									<span class="relative inline-flex rounded-full h-2 w-2 bg-teal-500"></span>
								</span>
							{/if}
						</li>
					{/each}

					{#if session}
						<li class="cursor-pointer">
							<a href="/account" class="flex gap-2 items-center text-lilac-500">
								<CircleUserRound /><span
									class="hover:underline underline-offset-4 decoration-2 decoration-lilac-500 text-slate-700"
								>
									Hi, {user?.first_name}</span
								>
							</a>
						</li>
						{#if user?.subscription_id}
							<li>
								<a
									href="/account"
									class="inline-flex rounded-xl py-2 px-6 bg-lilac-500 text-white hover:bg-lilac-700 transition-colors items-center gap-2"
									aria-current="page">Account</a
								>
							</li>
						{:else}
							<li>
								<a
									href={buildUrl('/#pricing')}
									class="block rounded-xl py-2 px-6 bg-lilac-500 text-white hover:bg-lilac-700 transition-colors"
									aria-current="page">Get started</a
								>
							</li>
						{/if}
					{:else}
						<li>
							<a
								href={buildUrl('/sign-in')}
								class="block rounded-xl py-2 px-6 hover:bg-gray-300 border border-gray-900/20 hover:bg-transparent transition-colors"
								aria-current="page">Sign in</a
							>
						</li>
						<li>
							<a
								href={buildUrl('/sign-in')}
								class="block rounded-xl py-2 px-6 bg-lilac-500 text-white hover:bg-lilac-700 transition-colors"
								aria-current="page">Get started</a
							>
						</li>
					{/if}
				</ul>
			</div>
		</div>
	</div>
	<div class="relative md:hidden flex justify-center">
		{#if mobileMenuOpen}
			<div
				class="w-11/12 md:block md:w-auto top-3 absolute"
				class:hidden={!mobileMenuOpen}
				transition:fade={{ duration: 100 }}
			>
				<ul
					class="font-medium flex flex-col p-4 border border-gray-100 rounded-lg bg-white shadow-xl"
				>
					{#each MOBILE_MENU_ITEMS(Boolean(session), Boolean(user?.subscription_id)) as menuItem, i}
						<li
							class="[&:nth-of-type(6)>a]:bg-lilac-500 [&:nth-of-type(6)>a]:mt-4 [&:nth-of-type(6)>a]:text-white [&>a]:last:border [&>a]:last:border-gray-900/20 [&>a]:last:mt-4"
						>
							<MobileMenuButton href={buildUrl(menuItem.href)}>
								{#if Boolean(session) && menuItem.title === 'Account'}
									<CircleUserRound />
								{/if}
								{menuItem.title}
							</MobileMenuButton>
						</li>
						{#if i === 4}
							<hr class="border border-slate-200 mt-4" />
						{/if}
					{/each}
				</ul>
			</div>
		{/if}
	</div>
</nav>

{@render children?.()}

<Footer />
